import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "employee-course-dashboard" }
const _hoisted_2 = {
  key: 0,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
}
const _hoisted_3 = {
  key: 1,
  class: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
}
const _hoisted_4 = { class: "flex items-center gap-2" }
const _hoisted_5 = { class: "text-red-700 dark:text-red-300" }
const _hoisted_6 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
}
const _hoisted_7 = { class: "h-60 bg-gradient-to-br from-primary/20 to-primary/5 dark:from-primary/30 dark:to-primary/10 flex items-center justify-center relative overflow-hidden" }
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = { class: "p-5" }
const _hoisted_10 = { class: "text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate" }
const _hoisted_11 = { class: "text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2" }
const _hoisted_12 = { class: "mb-2" }
const _hoisted_13 = { class: "flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1" }
const _hoisted_14 = { class: "w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2" }
const _hoisted_15 = { class: "flex items-center gap-2 mt-4" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = ["onClick"]
const _hoisted_18 = {
  key: 3,
  class: "text-center py-12"
}

import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { dashboardApi } from '@/services/dashboard'
import { useTitle } from '@/composables/ui/useTitle'
import type { EmployeeCourse } from '@/services/courseService'
import { GraduationCap, PlayCircle, Award, AlertCircle, Eye } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeCourseDashboardView',
  setup(__props) {

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
useTitle(`Courses - ${appTitle.value}`)

const router = useRouter()
const courses = ref<EmployeeCourse[]>([])
const loading = ref(true)
const error = ref('')

function isCourseCompleted(course: EmployeeCourse): boolean {
  return course.overall_progress >= 100
}

function statusBadgeClass(course: EmployeeCourse) {
  if (isCourseCompleted(course)) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
  switch (course.status) {
    case 0: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    case 1: return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    case 2: return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
    default: return ''
  }
}

function startCourse(course: EmployeeCourse) {
  router.push({ name: 'course-content', params: { courseId: course.course_id } })
}

// TEMPORARILY COMMENTED: viewCertificate
// function viewCertificate(course: EmployeeCourse) {
//   router.push({ name: 'my-certificates' })// }

async function fetchCourses() {
  loading.value = true
  error.value = ''
  try {
    courses.value = await dashboardApi.getEmployeeCourses()
  } catch (e: any) {
    error.value = e?.message || 'Failed to load courses'
  } finally {
    loading.value = false
  }
}

onMounted(fetchCourses)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "mb-6" }, [
      _createElementVNode("h1", { class: "text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2" }, "Courses"),
      _createElementVNode("p", { class: "text-gray-500 dark:text-gray-400" }, "Complete your training courses and earn certificates.")
    ], -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (i) => {
            return _createElementVNode("div", {
              key: i,
              class: "card p-0 overflow-hidden animate-pulse"
            }, [...(_cache[0] || (_cache[0] = [
              _createStaticVNode("<div class=\"h-40 bg-gray-200 dark:bg-gray-600\"></div><div class=\"p-5 space-y-3\"><div class=\"h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4\"></div><div class=\"h-3 bg-gray-200 dark:bg-gray-600 rounded w-full\"></div><div class=\"h-2 bg-gray-200 dark:bg-gray-600 rounded-full w-full\"></div><div class=\"h-9 bg-gray-200 dark:bg-gray-600 rounded-lg w-28\"></div></div>", 2)
            ]))])
          }), 64))
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-500" }),
              _createElementVNode("span", _hoisted_5, _toDisplayString(error.value), 1)
            ]),
            _createElementVNode("button", {
              onClick: fetchCourses,
              class: "mt-2 text-sm text-red-600 underline"
            }, "Try again")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(courses.value, (course) => {
              return (_openBlock(), _createElementBlock("div", {
                key: course.course_id,
                class: "card p-0 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group"
              }, [
                _createElementVNode("div", _hoisted_7, [
                  (course.cover_image)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: course.cover_image,
                        alt: course.name,
                        class: "w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      }, null, 8, _hoisted_8))
                    : (_openBlock(), _createBlock(_unref(GraduationCap), {
                        key: 1,
                        class: "w-16 h-16 text-primary/40"
                      })),
                  _createElementVNode("span", {
                    class: _normalizeClass(["absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold shadow", statusBadgeClass(course)])
                  }, _toDisplayString(isCourseCompleted(course) ? 'Completed' : course.status_name), 3)
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("h3", _hoisted_10, _toDisplayString(course.name), 1),
                  _createElementVNode("p", _hoisted_11, _toDisplayString(course.description || 'No description'), 1),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("span", null, _toDisplayString(course.completed_lessons) + "/" + _toDisplayString(course.total_lessons) + " lessons", 1),
                      _createElementVNode("span", null, _toDisplayString(Math.round(course.overall_progress)) + "%", 1)
                    ]),
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["h-2 rounded-full transition-all duration-500", course.overall_progress >= 100 ? 'bg-green-500' : 'bg-primary']),
                        style: _normalizeStyle({ width: course.overall_progress + '%' })
                      }, null, 6)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_15, [
                    (isCourseCompleted(course))
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: "btn btn-primary btn-sm flex items-center gap-1.5",
                          onClick: ($event: any) => (startCourse(course))
                        }, [
                          _createVNode(_unref(Eye), { class: "w-4 h-4" }),
                          _cache[1] || (_cache[1] = _createTextVNode(" View ", -1))
                        ], 8, _hoisted_16))
                      : (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          class: "btn btn-primary btn-sm flex items-center gap-1.5",
                          onClick: ($event: any) => (startCourse(course))
                        }, [
                          _createVNode(_unref(PlayCircle), { class: "w-4 h-4" }),
                          _createTextVNode(" " + _toDisplayString(course.status === 1 && course.overall_progress > 0 ? 'Resume' : 'Start'), 1)
                        ], 8, _hoisted_17))
                  ])
                ])
              ]))
            }), 128))
          ])),
    (!loading.value && !error.value && courses.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
          _createVNode(_unref(GraduationCap), { class: "w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" }),
          _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "text-lg font-semibold text-gray-600 dark:text-gray-400" }, "No courses available", -1)),
          _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-sm text-gray-400 mt-1" }, "Check back later for new training courses.", -1))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})